import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { 
  RefreshCcw, 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  XCircle,
  Play,
  Pause,
  RotateCcw,
  Loader2,
  Filter
} from "lucide-react";

export default function AIJobsMonitor() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [statusFilter]);

  const fetchJobs = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/admin/jobs`, {
        params,
        withCredentials: true
      });
      setJobs(response.data);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const handleRetry = async (jobId) => {
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/retry`, {}, {
        withCredentials: true
      });
      toast.success("Job queued for retry");
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to retry job");
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/cancel`, {}, {
        withCredentials: true
      });
      toast.success("Job cancelled");
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to cancel job");
    }
  };

  const handleRefund = async (jobId) => {
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/refund`, {}, {
        withCredentials: true
      });
      toast.success("Job refunded and credits restored");
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to refund job");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      processing: { color: "bg-blue-100 text-blue-800", icon: Loader2 },
      completed: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      failed: { color: "bg-red-100 text-red-800", icon: XCircle },
      cancelled: { color: "bg-gray-100 text-gray-800", icon: Pause }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">AI Jobs Monitor</h1>
            <p className="text-neutral-600">Monitor and manage AI generation jobs</p>
          </div>
          <Button onClick={() => { fetchJobs(); fetchStats(); }} variant="outline" className="gap-2">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-neutral-200">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-neutral-900">{stats.total || 0}</p>
                  <p className="text-sm text-neutral-500">Total Jobs</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-700">{stats.pending || 0}</p>
                  <p className="text-sm text-yellow-600">Pending</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-700">{stats.processing || 0}</p>
                  <p className="text-sm text-blue-600">Processing</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-700">{stats.completed || 0}</p>
                  <p className="text-sm text-green-600">Completed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-700">{stats.failed || 0}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cost & Performance Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total GPU Time</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {stats.total_gpu_seconds ? (stats.total_gpu_seconds / 60).toFixed(1) : 0} min
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Total Cost</p>
                    <p className="text-2xl font-bold text-green-900">
                      ₹{stats.total_cost ? stats.total_cost.toFixed(2) : '0.00'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <span className="text-green-600 text-xl font-bold">₹</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Avg Cost/Job</p>
                    <p className="text-2xl font-bold text-blue-900">
                      ₹{stats.total && stats.total_cost ? (stats.total_cost / stats.total).toFixed(2) : '0.00'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-neutral-500" />
          {["all", "pending", "processing", "completed", "failed"].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className="capitalize"
            >
              {status}
            </Button>
          ))}
        </div>

        {/* Jobs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Job Queue</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                No jobs found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Job ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Priority</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Attempts</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Created</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-neutral-700">
                            {job.id?.slice(-8) || "N/A"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="capitalize">
                            {job.job_type || "unknown"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(job.status)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={
                            job.priority === "high" ? "border-red-300 text-red-700" :
                            job.priority === "normal" ? "border-blue-300 text-blue-700" :
                            "border-gray-300 text-gray-700"
                          }>
                            {job.priority || "normal"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-neutral-700">{job.attempts || 0}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-neutral-500">
                            {job.created_at ? formatDate(job.created_at) : "N/A"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {job.status === "failed" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRetry(job.id)}
                                  className="gap-1"
                                >
                                  <RotateCcw className="w-3 h-3" /> Retry
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRefund(job.id)}
                                  className="gap-1 text-amber-600 hover:text-amber-700"
                                >
                                  Refund
                                </Button>
                              </>
                            )}
                            {(job.status === "pending" || job.status === "processing") && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancel(job.id)}
                                className="gap-1 text-red-600 hover:text-red-700"
                              >
                                <XCircle className="w-3 h-3" /> Cancel
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
